import { useCallback, useEffect, useState } from 'react';

import { getGameEngine } from '../GameEngine';
import {
  DEFAULT_SOUND_SETTINGS,
  type SoundSettings,
} from '../services/SoundEngine';
import {
  SOUND_SETTINGS_STORAGE_KEY,
  applySoundSettingsDefaults,
  loadSoundSettings,
  saveSoundSettings,
} from '../utils/SoundSettingsStorage';

export interface UseSoundSettingsResult {
  soundSettings: SoundSettings;
  setSoundSettings: (
    value: SoundSettings | ((prev: SoundSettings) => SoundSettings)
  ) => void;
  resetSoundSettings: () => void;
}

export const useSoundSettings = (): UseSoundSettingsResult => {
  const [soundSettings, setSoundSettingsState] = useState<SoundSettings>(() =>
    loadSoundSettings()
  );

  const applyToEngine = useCallback((settings: SoundSettings) => {
    try {
      getGameEngine().setSoundSettings(settings);
    } catch {
      // Ignore attempts to update the engine before it is ready (tests/SSR).
    }
  }, []);

  const updateState = useCallback(
    (updater: SoundSettings | ((prev: SoundSettings) => SoundSettings)) => {
      setSoundSettingsState(prevState => {
        const nextState =
          typeof updater === 'function'
            ? (updater as (prev: SoundSettings) => SoundSettings)(prevState)
            : updater;
        const normalized = applySoundSettingsDefaults(nextState);
        saveSoundSettings(normalized);
        applyToEngine(normalized);
        return normalized;
      });
    },
    [applyToEngine]
  );

  useEffect(() => {
    applyToEngine(soundSettings);
  }, [applyToEngine, soundSettings]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SOUND_SETTINGS_STORAGE_KEY) {
        setSoundSettingsState(loadSoundSettings());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const resetSoundSettings = useCallback(() => {
    updateState(DEFAULT_SOUND_SETTINGS);
  }, [updateState]);

  return {
    soundSettings,
    setSoundSettings: updateState,
    resetSoundSettings,
  };
};

export default useSoundSettings;
